import { Menu, MenuRoot } from "@base-ui/react/menu"
import React, { useMemo, useState } from "react"

import classNames from "classnames"

import { stopPropagation } from "shared/lib/stopPropagation"
import { useEvent } from "shared/lib/useEvent"
import { useFocusState } from "shared/lib/useFocusState"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { Action } from "shared/types/Action"
import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import "shared/ui/menu/styles/_menu.scss"
import { Tooltip } from "shared/ui/tooltip/Tooltip"

import { TabBarTabContainerContext } from "../context/TabBarTabContainerContext"
import { useTabBarBaseTabViewModel } from "../model/useTabBarBaseTabViewModel"
import {
  type TabBarBaseTabDnDOverrides,
  TabBarTabButton,
  type TabBarTabDivProps
} from "./TabBarBaseTab"
import type { TabBarTabProps } from "./TabBarTab"
import {
  TabBarTabFocusContext,
  TabBarTabTooltipContent
} from "./TabBarTabTooltipContent"

// Variant used as Menu.Trigger render target — Base UI overwrites ref via cloneElement,
// so we accept the DnD/focus refs through innerRef and merge them with the trigger ref.
// Menu.Trigger also injects onMouseDown and onTouchStart via cloneElement, which would
// overwrite the DnD drag-activation listeners. We intercept them and compose both.
export const TabBarTabButtonTrigger = React.forwardRef<
  HTMLDivElement,
  TabBarTabDivProps & {
    innerRef: React.Ref<HTMLDivElement>
    onMouseDown?: React.MouseEventHandler<HTMLDivElement>
    onTouchStart?: React.TouchEventHandler<HTMLDivElement>
  }
>(
  (
    { innerRef, onMouseDown, onTouchStart, dndListeners, ...props },
    triggerRef
  ) => {
    const ref = useMergedRefs(innerRef, triggerRef)

    const handleMouseDown = useEvent((e: React.MouseEvent<HTMLDivElement>) => {
      dndListeners?.onMouseDown?.(e)
      onMouseDown?.(e)
    })

    const handleTouchStart = useEvent((e: React.TouchEvent<HTMLDivElement>) => {
      dndListeners?.onTouchStart?.(e)
      onTouchStart?.(e)
    })

    const mergedDndListeners = useMemo(() => {
      return {
        ...dndListeners,
        onMouseDown: handleMouseDown,
        onTouchStart: handleTouchStart
      }
    }, [dndListeners])

    return (
      <TabBarTabButton ref={ref} dndListeners={mergedDndListeners} {...props} />
    )
  }
)

TabBarTabButtonTrigger.displayName = "TabBar.TabBarTabButtonTrigger"

type TabBarMenuTabProps = Omit<TabBarTabProps, "actions"> &
  TabBarBaseTabDnDOverrides & { actions: Action[] }

export const TabBarMenuTab = React.memo(
  React.forwardRef<HTMLDivElement, TabBarMenuTabProps>(
    (
      {
        eventKey,
        index,
        label,
        icon,
        count,
        truncationThreshold,
        className,
        disabled,
        variant,
        "aria-controls": ariaControls,
        "aria-label": ariaLabel,
        actions,
        dndRef,
        dndListeners,
        isDragging = false,
        insertBefore = false,
        insertAfter = false,
        isSortable = false
      },
      ref
    ) => {
      const {
        selected,
        displayLabel,
        contextValue,
        tabRef,
        tabId,
        onTabSelect
      } = useTabBarBaseTabViewModel({
        eventKey,
        index,
        label,
        count,
        truncationThreshold,
        isDragging,
        insertBefore,
        insertAfter
      })

      const [menuOpen, setMenuOpen] = useState(false)
      const menuHandle = useMemo(() => Menu.createHandle(), [])
      const { ref: focusRef, isFocused } = useFocusState<HTMLDivElement>()

      const mergedRefs = useMergedRefs(tabRef, ref, dndRef, focusRef)

      const handleClick = useEvent((e: React.MouseEvent<HTMLDivElement>) => {
        if (
          e.target instanceof Element &&
          e.target.closest("[data-tab-kebab]")
        ) {
          e.stopPropagation()
        } else if (!disabled) {
          onTabSelect(eventKey)
        }
      })

      const handleKeyDown = useEvent(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            if (e.target !== e.currentTarget) return
            e.preventDefault()
            onTabSelect(eventKey)
          } else if (
            (e.key === "F10" && e.shiftKey) ||
            (e.key === "ArrowDown" && e.altKey)
          ) {
            e.preventDefault()
            const triggerId = tabRef.current?.id
            if (triggerId) menuHandle.open(triggerId)
          }
        }
      )

      const handleMenuOpenChange = useEvent(
        (open: boolean, event: MenuRoot.ChangeEventDetails) => {
          if (open) {
            const isKebabMouseClick =
              event.event instanceof MouseEvent &&
              event.event.target instanceof Element &&
              !!event.event.target.closest("[data-tab-kebab]")
            const isImperative = event.reason === "imperative-action"
            if (!isKebabMouseClick && !isImperative) {
              event.cancel()
              return
            }
          }
          setMenuOpen(open)
          if (!open && event.reason === "escape-key") tabRef.current?.focus()
        }
      )

      const tabButtonProps: TabBarTabDivProps = {
        tabId,
        selected,
        ariaControls,
        ariaLabel,
        disabled,
        hasActions: true,
        menuOpen,
        className,
        isSortable,
        variant,
        index,
        isDragging,
        insertBefore,
        insertAfter,
        icon,
        count,
        displayLabel,
        dndListeners,
        onClick: handleClick,
        onKeyDown: handleKeyDown
      }

      return (
        <TabBarTabContainerContext.Provider value={contextValue}>
          <TabBarTabFocusContext.Provider value={isFocused}>
            <Menu.Root handle={menuHandle} onOpenChange={handleMenuOpenChange}>
              <Tooltip
                content={<TabBarTabTooltipContent text={label} />}
                placement="top"
              >
                <Menu.Trigger
                  id={tabId}
                  nativeButton={false}
                  render={
                    <TabBarTabButtonTrigger
                      innerRef={mergedRefs}
                      {...tabButtonProps}
                    />
                  }
                />
              </Tooltip>
              <Menu.Portal>
                <Menu.Positioner
                  className="menu__positioner"
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  positionMethod="fixed"
                  collisionAvoidance={{ align: "flip", side: "flip" }}
                >
                  <Menu.Popup
                    className="menu__popup"
                    aria-labelledby={tabId}
                    onClick={stopPropagation}
                  >
                    {actions.map(action => (
                      <React.Fragment key={action.id ?? action.label}>
                        <Menu.Item
                          className={classNames(
                            "btn",
                            "btn-menu",
                            "menu__item",
                            {
                              "btn--danger": action.isDanger
                            }
                          )}
                          disabled={action.disabled}
                          onClick={
                            action.onClick as React.MouseEventHandler<HTMLElement>
                          }
                        >
                          {action.icon ? (
                            <Icon name={action.icon} size={IconSize.SMALL} />
                          ) : null}
                          <span>{action.label}</span>
                        </Menu.Item>
                      </React.Fragment>
                    ))}
                  </Menu.Popup>
                </Menu.Positioner>
              </Menu.Portal>
            </Menu.Root>
          </TabBarTabFocusContext.Provider>
        </TabBarTabContainerContext.Provider>
      )
    }
  )
)

TabBarMenuTab.displayName = "TabBar.MenuTab"
